import { css } from "@emotion/css";
import { borderRadius, fontWeight, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LiveStatusSummaryResource, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { LiveStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcon";
import TaskStatusIcon from "~/areas/projects/components/TaskStatusIcon/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import DateFormatter from "~/utils/DateFormatter";
import { liveStatusSummaryText } from "./liveStatusCalculators";
interface LiveStatusSummaryBarProps {
    spaceId: string;
    projectSlug: string;
    liveStatusSummary: LiveStatusSummaryResource | null;
    latestDeployment: TimelineItem | null;
}
export function LiveStatusSummaryBar({ spaceId, projectSlug, liveStatusSummary, latestDeployment }: LiveStatusSummaryBarProps) {
    function LiveStatusContent() {
        if (!liveStatusSummary) {
            return <></>;
        }
        return (<div data-chameleon-tour="application-live-status" className={styles.iconStatusCard}>
                <div className={styles.iconStatusCardIcon}>
                    <LiveStatusIcon status={liveStatusSummary.Status} size={48}/>
                </div>
                <div className={styles.iconStatusCardTitle}>{liveStatusSummary.Status}</div>
                <div className={styles.iconStatusCardStatus}>Last updated {DateFormatter.momentAgo(liveStatusSummary.LastUpdated.toString())}</div>
            </div>);
    }
    function LastDeploymentContent() {
        if (!latestDeployment) {
            return <></>;
        }
        const deploymentStatusText = !latestDeployment.Completed ? <span>Started {DateFormatter.momentAgo(latestDeployment.Created.toString())}</span> : <span>Completed {DateFormatter.momentAgo(latestDeployment.Completed?.toString() ?? "")}</span>;
        return (<InternalLink to={links.deploymentPage.generateUrl({ spaceId, projectSlug, deploymentId: latestDeployment.Id, releaseVersion: latestDeployment.Version })}>
                <div data-chameleon-tour="last-deployment" className={styles.iconStatusCard}>
                    <div className={styles.iconStatusCardIcon}>
                        <TaskStatusIcon size={48} item={{ HasPendingInterruptions: latestDeployment.HasPendingInterruptions, HasWarningsOrErrors: latestDeployment.HasWarningsOrErrors, IsCompleted: latestDeployment.IsCompleted, State: latestDeployment.TaskState }}/>
                    </div>
                    <div className={styles.iconStatusCardTitle}>
                        {latestDeployment.Version} {latestDeployment.Iteration}
                    </div>
                    <div className={styles.iconStatusCardStatus}>{deploymentStatusText}</div>
                </div>
            </InternalLink>);
    }
    function StatusSummaryContent() {
        if (!latestDeployment?.TaskState || !liveStatusSummary?.Status) {
            return <></>;
        }
        return <span>{liveStatusSummaryText(latestDeployment.TaskState, liveStatusSummary.Status)}</span>;
    }
    return (<div className={styles.summaryBar}>
            <LiveStatusCard title="Live Status">{LiveStatusContent()}</LiveStatusCard>
            <LiveStatusCard title="Last Deployment">{LastDeploymentContent()}</LiveStatusCard>
            <LiveStatusCard title="Status Summary">{StatusSummaryContent()}</LiveStatusCard>
        </div>);
}
interface LiveStatusCardProps {
    title: string;
    children: JSX.Element;
}
export function LiveStatusCard(props: LiveStatusCardProps) {
    return (<div className={styles.liveStatusCard}>
            <div className={styles.liveStatusCardTitle}>{props.title}</div>
            <div>{props.children}</div>
        </div>);
}
const styles = {
    summaryBar: css({
        background: themeTokens.color.background.secondary.default,
        display: "flex",
        flexDirection: "row",
        marginBottom: space[8],
    }),
    liveStatusCard: css({
        background: themeTokens.color.background.primary.default,
        flex: 1,
        height: "120px",
        borderRadius: borderRadius.large,
        margin: space[16],
        padding: space[16],
        boxShadow: themeTokens.shadow.small,
    }),
    liveStatusCardTitle: css({
        font: text.heading.xSmall,
        lineHeight: lineHeight.xSmall,
        color: themeTokens.color.text.secondary,
        fontWeight: fontWeight[700],
        textTransform: "uppercase",
        marginBottom: space[12],
    }),
    iconStatusCard: css({
        display: "grid",
        gridTemplateColumns: "60px auto",
        gridTemplateRows: "24px 24px",
    }),
    iconStatusCardIcon: css({
        gridRowStart: 1,
        gridRowEnd: 2,
    }),
    iconStatusCardTitle: css({
        gridRow: 1,
        gridColumn: 2,
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    iconStatusCardStatus: css({
        gridRow: 2,
        gridColumn: 2,
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
};
